import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Alert,
  Spinner,
  Form,
  Row,
  Col,
  InputGroup,
  Pagination
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaProjectDiagram,
  FaList,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header";
import ProjectService from "../../../services/projectService";

const ManageProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination & Search States
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Taxonomy Modal States
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxType, setTaxType] = useState("category"); // "category" or "language"
  const [taxName, setTaxName] = useState("");
  const [savingTax, setSavingTax] = useState(false);

  // View Taxonomy Modal States
  const [showViewTaxModal, setShowViewTaxModal] = useState(false);
  const [taxList, setTaxList] = useState([]);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await ProjectService.getAllProjects();
      if (response.data && response.data.data && response.data.data.projects) {
        setProjects(response.data.data.projects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTaxModal = (type) => {
    setTaxType(type);
    setTaxName("");
    setShowTaxModal(true);
  };

  const handleSaveTaxonomy = async () => {
    if (!taxName.trim()) return;
    try {
      setSavingTax(true);
      if (taxType === "category") {
        await ProjectService.adminCreateCategory({ name: taxName.trim() });
        toast.success("Category added successfully");
      } else {
        await ProjectService.adminCreateLanguage({ name: taxName.trim() });
        toast.success("Tech Stack added successfully");
      }
      setShowTaxModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Failed to add taxonomy");
    } finally {
      setSavingTax(false);
    }
  };

  const handleOpenViewTaxModal = async (type) => {
    setTaxType(type);
    try {
      const res = await ProjectService.getProjectFilters();
      const data = res.data?.data || { categories: [], languages: [] };
      setTaxList(type === 'category' ? data.categories : data.languages);
      setShowViewTaxModal(true);
    } catch (error) {
      toast.error("Failed to load list");
    }
  };

  const handleDeleteTax = async (id) => {
    if (window.confirm("Are you sure you want to delete this?")) {
      try {
        if (taxType === "category") {
          await ProjectService.adminDeleteCategory(id);
        } else {
          await ProjectService.adminDeleteLanguage(id);
        }
        toast.success("Deleted successfully");
        setTaxList(taxList.filter(item => item.id !== id));
      } catch (error) {
        toast.error("Failed to delete");
      }
    }
  };

  // Pagination and Search Logic
  const filteredProjects = projects.filter((p) =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await ProjectService.adminDeleteProject(id);
        toast.success("Project deleted");
        loadProjects();
      } catch (error) {
        toast.error("Failed to delete project");
      }
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        <Card className="shadow-sm">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="mb-0 py-2">
              <FaProjectDiagram className="me-2" /> Project Management
            </h5>
            <div className="d-flex gap-2 py-2 flex-wrap">
              <Button variant="info" size="sm" onClick={() => handleOpenTaxModal('category')} className="text-white">
                <FaPlus className="me-1" /> Add Category
              </Button>
              <Button variant="outline-info" size="sm" onClick={() => handleOpenViewTaxModal('category')}>
                <FaList className="me-1" /> View Category
              </Button>
              <Button variant="info" size="sm" onClick={() => handleOpenTaxModal('language')} className="text-white">
                <FaPlus className="me-1" /> Add Tech Stack
              </Button>
              <Button variant="outline-info" size="sm" onClick={() => handleOpenViewTaxModal('language')}>
                <FaList className="me-1" /> View Tech Stack
              </Button>
              <Button variant="light" size="sm" onClick={() => navigate('/admin/projects/add')}>
                <FaPlus className="me-1" /> Add Project
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4} sm={12}>
                <InputGroup>
                  <Form.Control
                    placeholder="Search projects by title..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>

            {filteredProjects.length === 0 ? (
              <Alert variant="info" className="text-center">
                <FaProjectDiagram size={40} className="mb-3" />
                <h5>No projects found</h5>
                <p>Try a different search term or click "Add Project" to list one.</p>
              </Alert>
            ) : (
              <>
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>SL</th>
                      <th>Title</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProjects.map((p, index) => (
                      <tr key={p.id}>
                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td>{p.title}</td>
                        <td>₹{p.price}</td>
                        <td>
                          <Badge bg={p.status === "active" ? "success" : "warning"}>
                            {p.status}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => navigate(`/admin/projects/edit/${p.id}`)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(p.id)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <Pagination>
                      <Pagination.Prev 
                        disabled={currentPage === 1} 
                        onClick={() => handlePageChange(currentPage - 1)} 
                      />
                      {[...Array(totalPages)].map((_, i) => (
                        <Pagination.Item 
                          key={i + 1} 
                          active={i + 1 === currentPage}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next 
                        disabled={currentPage === totalPages} 
                        onClick={() => handlePageChange(currentPage + 1)} 
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Taxonomy Modal */}
      <Modal show={showTaxModal} onHide={() => setShowTaxModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{taxType === 'category' ? 'Add New Category' : 'Add New Tech Stack'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder={`Enter ${taxType} name`}
              value={taxName}
              onChange={(e) => setTaxName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveTaxonomy();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaxModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveTaxonomy} disabled={savingTax || !taxName.trim()}>
            {savingTax ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* View Taxonomy Modal */}
      <Modal show={showViewTaxModal} onHide={() => setShowViewTaxModal(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{taxType === 'category' ? 'Categories' : 'Tech Stacks'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {taxList.length === 0 ? (
            <p className="text-muted text-center py-4">No items found.</p>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {taxList.map((item, idx) => (
                  <tr key={item.id}>
                    <td>{idx + 1}</td>
                    <td>{item.name}</td>
                    <td className="text-center">
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTax(item.id)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewTaxModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageProjects;
